import React, { useState, useEffect, useCallback, useMemo } from "react";
import { colors } from "../shared/constants";
import { styles, inputStyle, labelStyle } from "../shared/styles";
import { formatDayLabel } from "../shared/dateUtils";
import { DateStrip } from "./DateStrip";

// One day at a time. Five-minute slots across a fortnight is over a thousand
// rows — a whole-campaign grid would be unreadable and slow to fetch.
export function SlotCalendar({
  client,
  campaignId,
  boardRows,
  onChanged,
  // Blocking a time is capacity management: the office decides how much of the
  // day it will give up. A manager has no /slots/ route, so for them the word
  // is a label rather than a control.
  canBlockSlots = true,
}) {
  const [days, setDays] = useState([]);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Separate from `error`, which is fatal and replaces the whole view. A
  // refused block should leave the day on screen.
  const [notice, setNotice] = useState("");
  const [viewing, setViewing] = useState(null);
  const [assigning, setAssigning] = useState(null);

  // `background` skips the loading flag. The early return below swaps the whole
  // day for a one-line "Loading..." div, so a plain reload after changing one
  // slot collapses the page and throws the scroll position away.
  const load = useCallback(
    (forDate, { background = false } = {}) => {
      if (!campaignId) return;
      if (!background) setLoading(true);
      setError("");
      const q = forDate ? `?date=${forDate}` : "";
      client
        .get(`/campaigns/${campaignId}/schedule/${q}`)
        .then((r) => {
          setDays(r.data?.days || []);
          setDate(r.data?.date || null);
          setSlots(r.data?.slots || []);
          setLoading(false);
        })
        .catch(() => {
          setError("Could not load the schedule.");
          setLoading(false);
        });
    },
    [client, campaignId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    load(date, { background: true });
    onChanged && onChanged();
  };

  const assigned = useMemo(
    () => slots.reduce((n, s) => n + s.assigned.length, 0),
    [slots]
  );
  const blocked = useMemo(
    () => slots.filter((s) => s.is_blocked).length,
    [slots]
  );

  // Applied locally first and never refetched: blocking changes nothing the
  // units board shows, and re-fetching the day would rebuild 60 rows and lose
  // your place. The server still has the final say — it refuses to block a slot
  // someone is booked into — so a rejection puts the row back.
  const applyBlocked = (slotId, value) => {
    setSlots((prev) =>
      prev.map((s) => (s.id === slotId ? { ...s, is_blocked: value, spaces_left: value ? 0 : s.capacity } : s))
    );
    setDays((prev) =>
      prev.map((d) =>
        d.date === date
          ? { ...d, blocked_count: (d.blocked_count || 0) + (value ? 1 : -1) }
          : d
      )
    );
  };

  const setBlocked = (slot, value) => {
    if (!canBlockSlots) return;
    setNotice("");
    applyBlocked(slot.id, value);
    client
      .patch(`/slots/${slot.id}/`, { is_blocked: value })
      .catch((e) => {
        applyBlocked(slot.id, !value);
        setNotice(
          e.response?.data?.is_blocked?.[0] ||
            `Could not ${value ? "block" : "unblock"} that time.`
        );
      });
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;
  if (error) return <div style={{ ...styles.loading, color: colors.red }}>{error}</div>;
  if (!days.length)
    return (
      <div style={{ ...styles.loading, lineHeight: "1.6" }}>
        This campaign has no inspection days. Days and times are set when a campaign
        is created — this one will need creating again.
      </div>
    );

  const rowCols = "150px 90px 1.5fr 1fr 80px";

  return (
    <>
      <DateStrip days={days} active={date} onSelect={load} />

      {notice && (
        <div style={{ color: colors.red, fontSize: "13px", padding: "0 4px 10px" }}>
          {notice}
        </div>
      )}

      <div style={styles.card}>
        <div
          style={{
            ...styles.tableHeader,
            gridTemplateColumns: rowCols,
            gap: "12px",
          }}
        >
          <div>Time</div>
          <div>Unit</div>
          <div>Tenancy</div>
          <div>Contact</div>
          <div />
        </div>

        {slots.map((s) => (
          <SlotRow
            key={s.id}
            slot={s}
            cols={rowCols}
            onOpen={(a) => setViewing({ slot: s, assigned: a })}
            onAssign={() => setAssigning(s)}
            onBlock={setBlocked}
            canBlockSlots={canBlockSlots}
          />
        ))}

        <div style={styles.count}>
          {assigned} of {slots.length} slot{slots.length !== 1 ? "s" : ""} booked on{" "}
          {formatDayLabel(date)}
          {blocked > 0 && ` · ${blocked} blocked`}
        </div>
      </div>

      {viewing && (
        <ContactDrawer
          client={client}
          slot={viewing.slot}
          row={viewing.assigned}
          canUnassign={canBlockSlots}
          onClose={() => setViewing(null)}
          onChanged={() => {
            setViewing(null);
            refresh();
          }}
        />
      )}

      {assigning && (
        <AssignUnit
          client={client}
          slot={assigning}
          campaignId={campaignId}
          boardRows={boardRows}
          onClose={() => setAssigning(null)}
          onAssigned={() => {
            setAssigning(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function SlotRow({ slot, cols, onOpen, onAssign, onBlock, canBlockSlots = true }) {
  const [hovered, setHovered] = useState(false);
  const [cellHover, setCellHover] = useState(false);
  const blocked = slot.is_blocked;
  const free = !blocked && slot.assigned.length === 0;
  const a = slot.assigned[0];
  const name = a ? a.contact_name || a.tenant_name || "" : "";
  const mobile = a ? a.mobile || a.tenant_mobile || "" : "";

  return (
    <div
      onClick={() => {
        if (blocked) return;
        return free ? onAssign() : onOpen(a);
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "grid",
        gridTemplateColumns: cols,
        gap: "12px",
        padding: "10px 20px",
        borderBottom: `1px solid ${colors.border}`,
        alignItems: "center",
        fontSize: "14px",
        cursor: blocked ? "default" : "pointer",
        backgroundColor: blocked
          ? "#fafafa"
          : hovered
          ? "#f8fafc"
          : colors.white,
      }}
    >
      <div style={{ color: colors.text, fontWeight: a ? "600" : "400" }}>
        {slot.label}
      </div>
      <div style={{ color: colors.text, fontWeight: "600" }}>
        {a ? a.unit_number : ""}
      </div>
      {a ? (
        <div
          style={{
            color: colors.text,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {a.unit_name || name || "—"}
        </div>
      ) : !canBlockSlots ? (
        // Read-only for a caller with no /slots/ route: the same word, so a
        // time that is not on offer still says why, but nothing to click.
        <div
          style={{
            color: colors.slate,
            fontStyle: blocked ? "normal" : "italic",
            fontWeight: blocked ? "600" : "400",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {blocked ? "Blocked" : "Available"}
        </div>
      ) : (
        // The word itself is the control — click Available to hold the time
        // back, click Blocked to give it up again.
        <button
          onClick={(e) => {
            e.stopPropagation();
            onBlock(slot, !blocked);
          }}
          onMouseEnter={() => setCellHover(true)}
          onMouseLeave={() => setCellHover(false)}
          title={blocked ? "Free this time up" : "Block this time"}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            textAlign: "left",
            font: "inherit",
            cursor: "pointer",
            color: colors.slate,
            fontStyle: blocked ? "normal" : "italic",
            fontWeight: blocked ? "600" : "400",
            textDecoration: cellHover ? "underline" : "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {blocked ? "Blocked" : "Available"}
        </button>
      )}
      <div style={{ color: colors.slate, whiteSpace: "nowrap" }}>{mobile || "—"}</div>
      <div
        style={{
          textAlign: "right",
          color: colors.slate,
          fontSize: "12px",
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.15s",
          whiteSpace: "nowrap",
        }}
      >
        {blocked ? "" : free ? "Assign" : "Details"}
      </div>
    </div>
  );
}

const drawerScrim = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.15)",
  zIndex: 99,
};

const drawerPanel = {
  position: "fixed",
  right: 0,
  top: 0,
  bottom: 0,
  width: "420px",
  maxWidth: "100vw",
  backgroundColor: colors.white,
  boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
  zIndex: 100,
  display: "flex",
  flexDirection: "column",
};

const drawerHeader = {
  padding: "20px 24px",
  borderBottom: `1px solid ${colors.border}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const closeButton = {
  background: "none",
  border: "none",
  fontSize: "20px",
  cursor: "pointer",
  color: "#999",
};

function ContactDrawer({ client, slot, row, canUnassign = true, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const unassign = async () => {
    setBusy(true);
    setError("");
    try {
      await client.patch(`/bookings/${row.booking_id}/`, { slot: null });
      onChanged();
    } catch (e) {
      setError("Could not free that slot.");
      setBusy(false);
    }
  };

  // The booking's own contact wins; the unit's contact is what we hold on file.
  const detail = (label, value, muted) =>
    value ? (
      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>{label}</label>
        <div style={{ fontSize: "14px", color: muted ? colors.slate : colors.text }}>
          {value}
        </div>
      </div>
    ) : null;

  const usingOnFile = !row.contact_name && !!row.tenant_name;

  return (
    <>
      <div onClick={onClose} style={drawerScrim} />
      <div style={drawerPanel}>
        <div style={drawerHeader}>
          <div>
            <div style={{ fontWeight: "700", fontSize: "18px", color: colors.text }}>
              Unit {row.unit_number}
            </div>
            <div style={{ fontSize: "12px", color: colors.slate }}>
              {slot.label} · {formatDayLabel(slot.date)}
            </div>
          </div>
          <button onClick={onClose} style={closeButton}>
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {detail("Tenancy", row.unit_name)}
          {detail("Floor", row.floor_level)}
          {detail(
            usingOnFile ? "Contact (on file)" : "Contact",
            row.contact_name || row.tenant_name,
            usingOnFile
          )}
          {detail("Mobile", row.mobile || row.tenant_mobile)}
          {detail("Email", row.email || row.tenant_email)}
          {detail("Access", row.choice_label)}
          {detail("Notes", row.notes)}

          {usingOnFile && (
            <div style={{ fontSize: "12px", color: colors.slate, marginTop: "4px" }}>
              These details come from the unit's contact record — they have not been
              confirmed for this booking.
            </div>
          )}

          {error && (
            <div style={{ color: colors.red, fontSize: "13px", marginTop: "12px" }}>
              {error}
            </div>
          )}
        </div>

        {canUnassign && (
        <div style={{ padding: "16px 24px", borderTop: `1px solid ${colors.border}` }}>
          <button
            onClick={unassign}
            disabled={busy}
            style={{
              background: "none",
              border: `1px solid ${colors.border}`,
              borderRadius: "6px",
              padding: "9px 20px",
              cursor: busy ? "default" : "pointer",
              fontSize: "14px",
              color: colors.slate,
            }}
          >
            {busy ? "Freeing..." : "Free this slot"}
          </button>
        </div>
        )}
      </div>
    </>
  );
}

function AssignUnit({ client, slot, campaignId, boardRows, onClose, onAssigned }) {
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Units without a time yet come first — those are the ones still to place.
  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (boardRows || [])
      .filter((r) =>
        !q
          ? true
          : `${r.unit_number} ${r.unit_name} ${r.contact_name} ${r.tenant_name}`
              .toLowerCase()
              .includes(q)
      )
      .sort((a, b) => (a.slot_id ? 1 : 0) - (b.slot_id ? 1 : 0));
  }, [boardRows, search]);

  const assign = async (row) => {
    setBusy(true);
    setError("");
    const payload = {
      campaign: campaignId,
      unit: row.unit_id,
      choice: "at_home",
      slot: slot.id,
    };
    try {
      if (row.booking_id) {
        await client.patch(`/bookings/${row.booking_id}/`, payload);
      } else {
        await client.post("/bookings/", {
          ...payload,
          contact_name: row.tenant_name || "",
          mobile: row.tenant_mobile || "",
          email: row.tenant_email || "",
        });
      }
      onAssigned();
    } catch (e) {
      const d = e.response?.data;
      setError(d?.slot?.[0] || d?.detail || "Could not assign that unit.");
      setBusy(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={drawerScrim} />
      <div style={drawerPanel}>
        <div style={drawerHeader}>
          <div>
            <div style={{ fontWeight: "700", fontSize: "18px", color: colors.text }}>
              Assign a unit
            </div>
            <div style={{ fontSize: "12px", color: colors.slate }}>
              {slot.label} · {formatDayLabel(slot.date)}
            </div>
          </div>
          <button onClick={onClose} style={closeButton}>
            ✕
          </button>
        </div>

        <div style={{ padding: "16px 24px 8px" }}>
          <input
            style={inputStyle}
            placeholder="Search unit or tenant..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {error && (
            <div style={{ color: colors.red, fontSize: "13px", marginTop: "10px" }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "0 12px 20px" }}>
          {options.map((r) => (
            <button
              key={r.unit_id}
              onClick={() => !busy && assign(r)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: "none",
                border: "none",
                borderBottom: `1px solid ${colors.border}`,
                padding: "10px 12px",
                cursor: busy ? "default" : "pointer",
                fontSize: "14px",
              }}
            >
              <div style={{ fontWeight: "600", color: colors.text }}>
                {r.unit_number}
                {r.unit_name ? ` · ${r.unit_name}` : ""}
              </div>
              <div style={{ fontSize: "12px", color: colors.slate, marginTop: "2px" }}>
                {r.slot_id
                  ? `Already at ${r.slot_label} on ${formatDayLabel(r.slot_date)}`
                  : r.contact_name || r.tenant_name || "No contact on file"}
              </div>
            </button>
          ))}
          {options.length === 0 && (
            <div style={{ ...styles.loading, fontSize: "14px" }}>Nothing matches.</div>
          )}
        </div>
      </div>
    </>
  );
}
